import HeadlineStrip from './evidence/HeadlineStrip';
import ShareTrend from './evidence/ShareTrend';
import CoveragePanel from './evidence/CoveragePanel';
import SeasonOverlay from './evidence/SeasonOverlay';
import StackedShareChart from './evidence/StackedShareChart';
import NewQueryList from './evidence/NewQueryList';
import SurfaceStat from './evidence/SurfaceStat';
import OpportunityTable from './evidence/OpportunityTable';
import MoverBars from './evidence/MoverBars';
import DeviceSplit from './evidence/DeviceSplit';
import AppearanceList from './evidence/AppearanceList';
import HealthSummary from './evidence/HealthSummary';
import DetailTable from './evidence/DetailTable';
import PositionBands from './evidence/PositionBands';

/**
 * Panel key → evidence component.
 *
 * Each component destructures the `facts` shape of the panels it serves and nothing
 * else (F22). There is deliberately no shared `Facts` type across panels: the backend
 * adds panel keys over time, and a global union would have to be widened for every new
 * one — turning a backend-only release into a front-end release, which is exactly what
 * F16 exists to prevent.
 *
 * A key that isn't here renders `GenericCard` (title + sentence) on the Detail tab.
 */
const EVIDENCE = {
  search_headline: HeadlineStrip,
  position_bands: PositionBands,
  discovery_breadth: ShareTrend,
  data_coverage: CoveragePanel,
  demand_season: SeasonOverlay,
  brand_split: StackedShareChart,
  new_queries: NewQueryList,
  discover_traffic: SurfaceStat,
  image_search: SurfaceStat,
  nearly_there: OpportunityTable,
  movers: MoverBars,
  top_queries: DetailTable,
  top_pages: DetailTable,
  query_page_map: DetailTable,
  device_split: DeviceSplit,
  search_appearance: AppearanceList,
  indexing_health: HealthSummary,
  sitemap_health: HealthSummary,
};

/** Evidence component for a panel key, or `null` when this release doesn't know it. */
export const evidenceFor = (panelKey) => EVIDENCE[panelKey] || null;

/**
 * Whether a panel has anything behind its expansion FOR THIS PAYLOAD (spec v2 §3).
 *
 * ── Why this is a per-component declaration and not a list here ─────────────────
 * A "Show more" that opens onto nothing is a control that lied, and the reader has no
 * way to tell it apart from one that is broken. Whether depth exists is a fact about the
 * evidence component and the rows it was handed — `device_split` gains a metrics table,
 * `discovery_breadth` gains nothing — so the component is the only place that can answer
 * it without a second copy of that knowledge drifting out of date here.
 *
 * ── Why the default is "no control" ─────────────────────────────────────────────
 * A component that declares nothing gets no control. That is the safe direction: a panel
 * key the backend adds later renders title + sentence + evidence and simply has no
 * expansion until someone writes one, which is a missing feature. The opposite default
 * would give every new panel a dead button, which is a visible bug — and §5 is explicit
 * that new panels must ship without a front-end release.
 *
 * Five components decline deliberately today: `ShareTrend` (discovery_breadth),
 * `CoveragePanel` (data_coverage), `StackedShareChart` (brand_split), `SurfaceStat`
 * (discover_traffic, image_search) and `AppearanceList` (search_appearance). Each already
 * renders everything its payload holds in the collapsed state.
 *
 * @param {string} panelKey the panel being rendered
 * @param {object} facts the panel's `facts`, model instructions already stripped
 * @returns {boolean} true when expansion would change what is on screen
 */
/**
 * True when a panel has nothing to show beyond its backend sentence, so it renders as
 * a GenericCard (title + sentence, no evidence area). Today: `movers` when the backend
 * marks the period not comparable — the sentence already says there is no last-year
 * data, and an evidence block under it only repeated that (or left an empty placeholder).
 * @param {string} panelKey
 * @param {object} facts
 * @returns {boolean}
 */
export const sentenceOnly = (panelKey, facts) =>
  panelKey === 'movers' && facts?.summary?.comparable === false;

export const hasDepth = (panelKey, facts) => {
  const Evidence = EVIDENCE[panelKey];
  return typeof Evidence?.hasDepth === 'function' && Evidence.hasDepth(facts) === true;
};

export default evidenceFor;
